import type { TFunction } from 'i18next';
import type { Dokumenttype, Plandokument, PlandokumentInput } from '@origo/planregister';
import type { DocumentFileKind, DocumentFormValues, DocumentItem } from '../types';
import { dokumenttypeLabel } from '../codelists/dokumenttype';
import { parseIsoDate } from './toProcessings';

const norwegianDateFormat = new Intl.DateTimeFormat('nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
});

const WORD_MIMETYPES = new Set([
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

/** Mimetype først; eldre dokumenter mangler den ofte, så fall tilbake til filendelse. */
export function toDocumentFileKind(doc: Pick<Plandokument, 'mimetype' | 'filnavn' | 'referansedokumentfil'>): DocumentFileKind {
    const mimetype = doc.mimetype?.toLowerCase();
    if (mimetype === 'application/pdf') return 'pdf';
    if (mimetype && WORD_MIMETYPES.has(mimetype)) return 'word';

    const name = (doc.filnavn || doc.referansedokumentfil || '').split(/[?#]/)[0].toLowerCase();
    if (name.endsWith('.pdf')) return 'pdf';
    if (name.endsWith('.doc') || name.endsWith('.docx')) return 'word';
    return 'other';
}

/** Nyeste øverst på dokumentdato. Dokumenter uten dato havner sist, i
 *  opprinnelig rekkefølge. Returnerer en ny liste. */
export function sortNewestFirst(docs: readonly Plandokument[]): Plandokument[] {
    return [...docs].sort(
        (a, b) => (b.dokumentdato?.getTime() ?? -Infinity) - (a.dokumentdato?.getTime() ?? -Infinity)
    );
}

export function toDocumentItem(doc: Plandokument, t: TFunction): DocumentItem {
    const dokumenttype = dokumenttypeLabel(doc.dokumenttype, t);
    // Legacy-dokumenter mangler ofte tittel og har den reelle tittelen i
    // beskrivelse — bruk den som tittel og ikke gjenta den under.
    const beskrivelseAsTitle = !doc.tittel && !!doc.beskrivelse;
    return {
        id: String(doc.objectId),
        dokumentnavn: doc.tittel || doc.beskrivelse || t('documents.untitled'),
        dokumenttype,
        dato: doc.dokumentdato ? norwegianDateFormat.format(doc.dokumentdato) : undefined,
        saksnummer: doc.saksnummer,
        beskrivelse: beskrivelseAsTitle ? undefined : doc.beskrivelse,
        url: doc.referansedokumentfil,
        filtype: toDocumentFileKind(doc),
        planbehUid: doc.planbehUid,
    };
}

export function emptyDocumentForm(): DocumentFormValues {
    return { tittel: '', dokumenttype: '', dato: '', beskrivelse: '' };
}

/** Hva et nytt dokument knyttes til i tillegg til planen (jf. spec F3.1.2 og F12.4). */
export interface DocumentLink {
    planbehUid?: string;
    dispensasjonId?: string;
}

/** `link` stemples inn når dokumentet opprettes fra en behandlings- eller
 *  dispensasjons-scopet visning. */
export function toPlandokumentInput(
    values: DocumentFormValues,
    napId: string,
    link: DocumentLink = {}
): PlandokumentInput {
    return {
        dokumentId: crypto.randomUUID(),
        nasjonalArealplanId: napId,
        planbehUid: link.planbehUid,
        dispensasjonId: link.dispensasjonId,
        tittel: values.tittel,
        dokumenttype: (values.dokumenttype || undefined) as Dokumenttype | undefined,
        dokumentdato: parseIsoDate(values.dato),
        beskrivelse: values.beskrivelse || undefined,
    };
}
